import FinancialRecordService from './financial_record_service';
import TaskService from './task_service';
import { TASK_STATUS, TASK_PRIORITY, TASK_TYPE } from '../constants/tasks';
import { FINANCIAL_TYPES, FINANCIAL_SOURCES, FINANCIAL_PAYMENT_METHODS, FINANCIAL_STATUS } from '../constants/financial';

/**
 * DebtPaymentService
 * 
 * Centraliza a lógica pesada de regras de negócio financeiras 
 * durante a quitação (parcial ou total) de saldos devedores de um cliente.
 * Anteriormente esta lógica ficava no hook useDebtPayment.
 */
const DebtPaymentService = {

    /**
     * Processa uma tentativa de quitação de saldo devedor ou a promessa de pagamento.
     * @param {Object} payload Parâmetros do processamento
     * @param {string} payload.idTenant
     * @param {Object} payload.user - AuthUser logado
     * @param {Array} payload.pendingDebts - Lista de dívidas pendentes originais
     * @param {Array} payload.payments - Lista de pagamentos efetuados no carrinho
     * @param {string} [payload.dueDate] - Data prometida (em caso de negociação sem pagamento)
     * @param {number} payload.totalPaid - Total sendo pago (somatório de payments)
     * @param {Object} payload.memberData - Dados básicos do cliente (id, nome, etc)
     * @param {Object} payload.consultantData - Dados do consultor da venda
     * @param {Array} payload.bankAccounts - Contas bancárias da franquia
     * @param {string} payload.saleDate - Data de registro base para as emissões (YYYY-MM-DD)
     */
    processDebtPayment: async ({
        idTenant,
        user,
        pendingDebts,
        payments,
        dueDate,
        totalPaid,
        memberData,
        consultantData,
        bankAccounts,
        saleDate
    }) => {
        if (!idTenant) throw new Error("ID do Tenant não informado.");

        const sortedDebts = [...pendingDebts].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        // 1) Caso seja apenas uma PROMESSA DE PAGAMENTO (sem nenhum valor pago hoje)
        if (payments.length === 0 && dueDate) {
            // Atualizar o vencimento de todas as dívidas pendentes
            for (const debt of pendingDebts) {
                await FinancialRecordService.save({
                    ...debt,
                    dueDate: dueDate,
                    notes: `Promessa de pagamento para ${dueDate}`
                }, idTenant, user, pendingDebts); // Reusing save function to trigger updates and audit
            }

            // Criar Tarefa na Dashboard Operacional
            const taskData = {
                title: `Cobrar Promessa de Pagamento`,
                description: `O cliente **${memberData.name}** renegociou o saldo devedor e prometeu o pagamento para o dia ${dueDate}.\nTratar a cobrança nesta data.`,
                type: TASK_TYPE.FINANCIAL,
                priority: TASK_PRIORITY.HIGH,
                status: TASK_STATUS.NEW,
                scheduledDate: dueDate,
                selectedMember: {
                    id: memberData.id,
                    fullName: memberData.name,
                },
                selectedEmployees: [{
                    id: user.uid,
                    fullName: user.displayName || user.name,
                    photoUrl: user.photoURL || ""
                }]
            };
            await TaskService.createTask(idTenant, user, taskData);

            return { isPromiseOnly: true, dueDate: dueDate };
        }


        // 2) Fluxo normal de QUITAÇÃO REAL (Parcial ou Total)
        // Adicionamos lançamentos "PAID" baseados nos métodos de pagamento inseridos no carrinho
        for (const payment of payments) {
            const feePercentage = payment.feeAmount ? (payment.feeAmount / payment.value * 100) : 0;
            const bankAccountId = payment.idBankAccount || bankAccounts[0]?.id || "";
            const bankAccountName = bankAccounts.find(b => b.id === bankAccountId)?.description || bankAccounts[0]?.description || "";

            await FinancialRecordService.save({
                type: FINANCIAL_TYPES.INCOME,
                idCategory: sortedDebts[0]?.idCategory || "",
                categoryName: sortedDebts[0]?.categoryName || "Quitação de Saldo",
                sourceType: FINANCIAL_SOURCES.MEMBER,
                idEntity: memberData.id,
                entityName: memberData.name,
                idMember: memberData.id,
                memberName: memberData.name,
                memberPhone: memberData.phone || null,
                memberEmail: memberData.email || null,
                memberPhotoUrl: memberData.photoUrl || null,
                
                idConsultant: consultantData.id || null,
                consultantName: consultantData.name || null,
                consultantEmail: consultantData.email || null,
                consultantPhone: consultantData.phone || null,
                consultantPhotoUrl: consultantData.photoUrl || null,
                
                idEmployee: user?.uid,
                employeeName: user?.displayName || user?.name || 'Sistema',
                employeeEmail: user?.email,
                employeePhone: user?.phone,
                employeePhotoUrl: user?.photoURL,
                
                grossAmount: payment.value,
                feePercentage: feePercentage,
                feeAmount: payment.feeAmount || 0,
                netAmount: payment.value - (payment.feeAmount || 0),
                issueDate: saleDate,
                dueDate: saleDate,
                competenceDate: saleDate,
                paymentDate: saleDate,
                idBankAccount: bankAccountId,
                bankAccountName: bankAccountName,
                paymentMethod: payment.methodId || FINANCIAL_PAYMENT_METHODS.CASH,
                status: FINANCIAL_STATUS.PAID,
                notes: "Quitação parcial/total de saldo devedor"
            }, idTenant, user);
        }

        // 3) Reduzir gradativamente as dívidas PENDING / OVERDUE originais
        let toReduce = totalPaid;
        
        for (const debt of sortedDebts) {
            const debtValue = debt.grossAmount;

            if (toReduce >= debtValue) {
                // Dívida quitada por completo, apagamos o Pending (pois já cobrimos com o PAID gerado)
                await FinancialRecordService.delete(debt.id, idTenant, user, "Apagado por quitação de saldo");
                toReduce -= debtValue;
            } else if (toReduce > 0) {
                // Dívida quitada parcialmente
                const updatedDebt = {
                    ...debt,
                    grossAmount: debt.grossAmount - toReduce,
                    netAmount: debt.netAmount - toReduce,
                };
                if (dueDate) updatedDebt.dueDate = dueDate;

                await FinancialRecordService.save(updatedDebt, idTenant, user, pendingDebts);
                toReduce = 0;
            } else if (dueDate) {
                // Se o componente preencheu dueDate (mas toReduce esgotou e não limpou esse Pending),
                // adiamos esse Pending para a data prometida
                await FinancialRecordService.save({
                    ...debt,
                    dueDate: dueDate
                }, idTenant, user, pendingDebts);
            }
        }

        return { isPromiseOnly: false, paidAmount: totalPaid };
    }
};

export default DebtPaymentService;
